"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { logger } from "@/lib/logger";
import { useRouter } from "next/navigation";
import { ConversationPreview } from "@/components/ConversationPreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BottomNav } from "@/components/bottom-nav";
import { Search, MessageSquare } from "lucide-react";
import { createClient } from "@/lib/supabase/client";

interface Conversation {
  conversation_id: string;
  other_participant_name: string;
  other_participant_avatar: string | null;
  last_message: string | null;
  last_message_time: string | null;
  unread_count: number;
  other_participant_is_online: boolean;
}

export default function MessagesPage() {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState<"contractor" | "worker">("worker");
  const supabase = createClient();

  useEffect(() => {
    fetchConversations();
    fetchUserType();

    // Subscribe to new messages for real-time updates
    const channel = supabase
      .channel('messages')
      .on('postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUserType = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("type")
        .eq("id", user.id)
        .single();
      if (profile?.type) {
        setUserType(profile.type as "contractor" | "worker");
      }
    }
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch('/api/messages');
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      logger.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredConversations = conversations.filter((conversation) =>
    conversation.other_participant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 pl-10 text-base"
              disabled
            />
          </div>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-pulse text-muted-foreground">Loading conversations...</div>
        </div>
        <BottomNav userType={userType} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Search Bar */}
      <div className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 pl-10 text-base"
          />
        </div>
      </div>

      {/* Conversations List */}
      <main className="mx-auto max-w-screen-sm">
        {filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery
                ? "No conversations match your search"
                : "Start a conversation by hiring a worker or applying for a job"}
            </p>
            {!searchQuery && (
              <Button asChild>
                <Link href="/dashboard">Go to Dashboard</Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {filteredConversations.map((conversation) => (
              <ConversationPreview
                key={conversation.conversation_id}
                id={conversation.conversation_id}
                name={conversation.other_participant_name}
                avatar={conversation.other_participant_avatar || undefined}
                lastMessage={conversation.last_message || "No messages yet"}
                lastMessageTime={conversation.last_message_time ? new Date(conversation.last_message_time) : new Date()}
                unreadCount={conversation.unread_count}
                isOnline={conversation.other_participant_is_online}
                onClick={() => router.push(`/messages/${conversation.conversation_id}`)}
              />
            ))}
          </div>
        )}
      </main>

      <BottomNav userType={userType} />
    </div>
  );
}
